import { FormControl, FormGroup, Validators } from '@angular/forms';
import { ActivatedRoute, Params, Router } from '@angular/router';
import { AfterViewInit, Component, ElementRef, HostListener, OnInit, ViewChild } from '@angular/core';
import { debounceTime, distinctUntilChanged, first, takeUntil } from 'rxjs/operators';
import { Observable, Subject } from 'rxjs';

import { FormHelper } from '@app/ui-forms/classes/form-helper';

import { CoreAPIService } from '@app/core/services/core-api.service';
import { ModalService } from '@app/core/services/modal.service';
import { SessionService } from '@app/core/services/session.service';
import { StorageService } from '@app/core/services/storage.service';
import { ToastService } from '@app/core/services/toast.service';

import { ErrorService } from '@app/core/services/error.service';
import { IFrameCommunicationsService } from '@app/core/services/iframe-communications.service';
import { UIDealerHeaderComponent } from '@app/ui/components/dealer-header/dealer-header.component';

@Component({
	selector: 'cng-auth',
	templateUrl: './authenticate.component.html',
	styleUrls: ['./authenticate.component.less'],
})
export class AuthenticateComponent implements OnInit, AfterViewInit {
	@ViewChild('content') content: ElementRef;
	@ViewChild(UIDealerHeaderComponent) headerComponent: UIDealerHeaderComponent;

	header: ElementRef;

	enquiry_hash: string;
	vehicle_id: string;
	auth_form: FormGroup;
	email_sending: boolean = false;
	sms_sending: boolean = false;
	email_disabled: boolean = true;
	sms_disabled: boolean = true;
	customer_email: string;
	customer_sms: string;
	verifying: boolean = false;
	has_error: boolean = false;
	vehicle_in_storage_id: number;
	trigger: string;
	success_path: string;
	forget_path: string;
	can_start_again: boolean = false;
	destroyed$: Subject<boolean> = new Subject<boolean>();
	query_params: Params;

	constructor(
		private core_api_service: CoreAPIService,
		private activated_route: ActivatedRoute,
		private session_service: SessionService,
		private storage_service: StorageService,
		private router: Router,
		private toast_service: ToastService,
		private modal_service: ModalService,
		private error_service: ErrorService,
		private iframe_communications_service: IFrameCommunicationsService,
	) {
		this.activated_route.params.pipe(first()).subscribe((params: Params) => {
			this.enquiry_hash = params['enquiry_hash'];
			this.vehicle_id = params['vehicle_id'];
		});

		this.activated_route.queryParams.pipe(first()).subscribe((params: Params) => {
			this.query_params = params;
		});

		this.activated_route.data.pipe(first()).subscribe((data) => {
			this.trigger = data.trigger;
			(this.success_path = data.success_path), (this.forget_path = data.forget_path), (this.can_start_again = data.can_start_again || false);
		});

		this.error_service.error$.pipe(takeUntil(this.destroyed$)).subscribe(() => {
			this.has_error = true;
		});

		this.vehicle_in_storage_id = this.getVehicleInStorageId();

		this.iframe_communications_service.sendDomainEvent('plugin.loaded', true);
	}

	/**
	 * Lifecycle hook: Prepared component and grabs custom details.
	 */
	ngOnInit(): void {
		this.modal_service.close();

		this.session_service.setTitle('Verifying');
		this.session_service.stopAppLoading();

		this.buildForm();

		this.activated_route.data.pipe(first()).subscribe((data) => {
			data = data[0];

			if (data && data[0]) {
				if (data[0].email) {
					this.customer_email = data[0].email;
					this.email_disabled = false;
				}

				if (data[0].mobile) {
					this.customer_sms = data[0].mobile;
					this.sms_disabled = false;
				}
			} else {
				this.has_error = true;
			}
		});

		this.communicateHeightDelayed();
	}

	ngAfterViewInit(): void {
		this.header = this.headerComponent.header;

		this.setupHeightMutationObserver();
		this.communicateHeight();
	}

	/**
	 * Builds authenticate form.
	 */
	buildForm(): void {
		this.auth_form = new FormGroup({
			access_code: new FormControl(null, Validators.required),
		});
	}

	/**
	 * Gets ID of vehicle in browser storage.
	 */
	getVehicleInStorageId(): number {
		const vehicles_in_storage: { key: string; value: string }[] = this.storage_service.getAllThatStartWith(
			'vehicle-'
		);

		if (vehicles_in_storage.length < 1) {
			return null;
		}

		const vehicle_in_storage: { key: string; value: string } = vehicles_in_storage.find(
			(storage_item: { key: string; value: string }) => {
				return storage_item.value === this.enquiry_hash;
			}
		);

		if (!vehicle_in_storage) {
			return null;
		}

		return parseInt(vehicle_in_storage.key.replace('vehicle-', ''));
	}

	/**
	 * Sends access code to customer's email or mobile.
	 *
	 * @param {string} channel
	 */
	sendAccessCode(channel: string): void {
		this.email_sending = channel === 'email';
		this.sms_sending = channel === 'sms';

		const body: { mail: boolean; sms: boolean; trigger: string } = {
			mail: this.email_sending,
			sms: this.sms_sending,
			trigger: this.trigger,
		};

		this.core_api_service.authenticate(this.enquiry_hash, body).subscribe(() => {
			if (this.email_sending) {
				this.email_disabled = true;
			}

			if (this.sms_sending) {
				this.sms_disabled = true;
			}

			this.email_sending = false;
			this.sms_sending = false;

			this.auth_form.controls['access_code'].setValue('');
			this.toast_service.success('Access code sent!');
			FormHelper.reset(this.auth_form);
		});
	}

	/**
	 * Submits auth form when access code is entered.
	 */
	onSubmit(): void {
		this.verifying = true;
		if (this.auth_form.invalid) {
			FormHelper.forceValidate(this.auth_form);
			this.toast_service.error('Form has invalid fields');
			this.verifying = false;
		} else {
			const body: any = {
				pin: this.auth_form.controls['access_code'].value,
			};

			this.core_api_service
				.verify(this.enquiry_hash, body)
				.pipe(first())
				.subscribe((response) => {
					if (response.status !== 200) {
						this.verifying = false;
						this.auth_form.controls['access_code'].setErrors({ invalid: true });
						this.email_disabled = false;
						this.sms_disabled = false;

						if (response.body && response.body.attempts === 0) {
							this.toast_service.error('No remaining attempts, please re-send code');
						} else {
							this.toast_service.error('Access code is invalid');
						}
					} else {
						this.storage_service.setItem('token-' + this.enquiry_hash, response.body.token);
						this.router
							.navigate([this.success_path.replace(':enquiry_hash', this.enquiry_hash).replace(':vehicle_id', this.vehicle_id)], {
								queryParams: this.query_params,
								queryParamsHandling: 'merge',
							})
							.then(() => {
								this.verifying = false;
							});
					}
				});
		}
	}

	/**
	 * Removes storage and starts again
	 */
	onStartAgain(): void {
		const new_params = { ...this.query_params };
		delete new_params['enquiry_hash'];

		this.storage_service.removeAllThatStartWith('token-');
		this.storage_service.removeAllThatStartWith('vehicle-');
		this.router
			.navigate([this.forget_path.replace(':vehicle_id', this.vehicle_in_storage_id.toString())], {
				queryParams: new_params,
				queryParamsHandling: 'merge',
			})
	}

	communicateHeightDelayed(): void {
		setTimeout(() => {
			this.communicateHeight();
		}, 100);
	}

	@HostListener('window:resize')
	communicateHeight(): void {
		let height: number = this.content.nativeElement.scrollHeight;

		height += this.header.nativeElement.scrollHeight;

		this.iframe_communications_service.communicateHeight(height);
	}

	setupHeightMutationObserver() {
		const observerable$ = new Observable<number>((observer) => {
			const callback = () => {
				observer.next(this.content.nativeElement.scrollHeight);
			};
			const elementObserver = new MutationObserver(callback);

			const config = { attributes: true, childList: true, subtree: true };
			elementObserver.observe(this.content.nativeElement, config);
		});

		observerable$
			.pipe(takeUntil(this.destroyed$))
			.pipe(debounceTime(25), distinctUntilChanged())
			.subscribe(() => {
				this.communicateHeight();
			});
	}
}
